import {
  specialistAvailabilityResponseSchema,
  specialistAvailabilitySchema,
  specialistCertificateListResponseSchema,
  specialistCertificateSchema,
  specialistEducationListResponseSchema,
  specialistEducationSchema,
  specialistLanguageListResponseSchema,
  specialistLanguageSchema,
  specialistPortfolioProjectListResponseSchema,
  specialistPortfolioProjectSchema,
  specialistProfileSchema,
  specialistProfileSkillListResponseSchema,
  specialistProfileSkillSchema,
  specialistWorkExperienceListResponseSchema,
  specialistWorkExperienceSchema,
  specialistWorkPreferencesResponseSchema,
  specialistWorkPreferencesSchema,
  type SpecialistAvailability,
  type SpecialistAvailabilityPutRequest,
  type SpecialistCertificate,
  type SpecialistCertificateCreateRequest,
  type SpecialistCertificateListResponse,
  type SpecialistCreateRequest,
  type SpecialistEducation,
  type SpecialistEducationCreateRequest,
  type SpecialistEducationListResponse,
  type SpecialistLanguage,
  type SpecialistLanguageCreateRequest,
  type SpecialistLanguageListResponse,
  type SpecialistPortfolioProject,
  type SpecialistPortfolioProjectCreateRequest,
  type SpecialistPortfolioProjectListResponse,
  type SpecialistProfile,
  type SpecialistProfileSkill,
  type SpecialistProfileSkillCreateRequest,
  type SpecialistProfileSkillListResponse,
  type SpecialistProfileUpdateRequest,
  type SpecialistWorkExperience,
  type SpecialistWorkExperienceCreateRequest,
  type SpecialistWorkExperienceListResponse,
  type SpecialistWorkPreferences,
  type SpecialistWorkPreferencesPutRequest,
} from "@brainics/api-contract";

import { requestJson } from "./api.js";

function jsonInit(method: string, payload: unknown): RequestInit {
  return {
    method,
    headers: { "content-type": "application/json" },
    body: JSON.stringify(payload),
  };
}

export async function createSpecialistProfile(
  input: SpecialistCreateRequest,
): Promise<SpecialistProfile> {
  return requestJson("/api/v1/specialists", specialistProfileSchema, jsonInit("POST", input));
}

export async function getSpecialistProfile(specialistId: string): Promise<SpecialistProfile> {
  return requestJson(`/api/v1/specialists/${specialistId}`, specialistProfileSchema);
}

export async function updateSpecialistProfile(
  specialistId: string,
  input: SpecialistProfileUpdateRequest,
): Promise<SpecialistProfile> {
  return requestJson(
    `/api/v1/specialists/${specialistId}`,
    specialistProfileSchema,
    jsonInit("PATCH", input),
  );
}

export async function listSpecialistSkills(
  specialistId: string,
): Promise<SpecialistProfileSkillListResponse> {
  return requestJson(
    `/api/v1/specialists/${specialistId}/skills`,
    specialistProfileSkillListResponseSchema,
  );
}

export async function createSpecialistSkill(
  specialistId: string,
  input: SpecialistProfileSkillCreateRequest,
): Promise<SpecialistProfileSkill> {
  return requestJson(
    `/api/v1/specialists/${specialistId}/skills`,
    specialistProfileSkillSchema,
    jsonInit("POST", input),
  );
}

export async function listSpecialistWorkExperiences(
  specialistId: string,
): Promise<SpecialistWorkExperienceListResponse> {
  return requestJson(
    `/api/v1/specialists/${specialistId}/work-experiences`,
    specialistWorkExperienceListResponseSchema,
  );
}

export async function createSpecialistWorkExperience(
  specialistId: string,
  input: SpecialistWorkExperienceCreateRequest,
): Promise<SpecialistWorkExperience> {
  return requestJson(
    `/api/v1/specialists/${specialistId}/work-experiences`,
    specialistWorkExperienceSchema,
    jsonInit("POST", input),
  );
}

export async function listSpecialistPortfolioProjects(
  specialistId: string,
): Promise<SpecialistPortfolioProjectListResponse> {
  return requestJson(
    `/api/v1/specialists/${specialistId}/portfolio-projects`,
    specialistPortfolioProjectListResponseSchema,
  );
}

export async function createSpecialistPortfolioProject(
  specialistId: string,
  input: SpecialistPortfolioProjectCreateRequest,
): Promise<SpecialistPortfolioProject> {
  return requestJson(
    `/api/v1/specialists/${specialistId}/portfolio-projects`,
    specialistPortfolioProjectSchema,
    jsonInit("POST", input),
  );
}

export async function listSpecialistEducations(
  specialistId: string,
): Promise<SpecialistEducationListResponse> {
  return requestJson(
    `/api/v1/specialists/${specialistId}/educations`,
    specialistEducationListResponseSchema,
  );
}

export async function createSpecialistEducation(
  specialistId: string,
  input: SpecialistEducationCreateRequest,
): Promise<SpecialistEducation> {
  return requestJson(
    `/api/v1/specialists/${specialistId}/educations`,
    specialistEducationSchema,
    jsonInit("POST", input),
  );
}

export async function listSpecialistCertificates(
  specialistId: string,
): Promise<SpecialistCertificateListResponse> {
  return requestJson(
    `/api/v1/specialists/${specialistId}/certificates`,
    specialistCertificateListResponseSchema,
  );
}

export async function createSpecialistCertificate(
  specialistId: string,
  input: SpecialistCertificateCreateRequest,
): Promise<SpecialistCertificate> {
  return requestJson(
    `/api/v1/specialists/${specialistId}/certificates`,
    specialistCertificateSchema,
    jsonInit("POST", input),
  );
}

export async function listSpecialistLanguages(
  specialistId: string,
): Promise<SpecialistLanguageListResponse> {
  return requestJson(
    `/api/v1/specialists/${specialistId}/languages`,
    specialistLanguageListResponseSchema,
  );
}

export async function createSpecialistLanguage(
  specialistId: string,
  input: SpecialistLanguageCreateRequest,
): Promise<SpecialistLanguage> {
  return requestJson(
    `/api/v1/specialists/${specialistId}/languages`,
    specialistLanguageSchema,
    jsonInit("POST", input),
  );
}

export async function getSpecialistWorkPreferences(specialistId: string) {
  return requestJson(
    `/api/v1/specialists/${specialistId}/work-preferences`,
    specialistWorkPreferencesResponseSchema,
  );
}

export async function putSpecialistWorkPreferences(
  specialistId: string,
  input: SpecialistWorkPreferencesPutRequest,
): Promise<SpecialistWorkPreferences> {
  return requestJson(
    `/api/v1/specialists/${specialistId}/work-preferences`,
    specialistWorkPreferencesSchema,
    jsonInit("PUT", input),
  );
}

export async function getSpecialistAvailability(specialistId: string) {
  return requestJson(
    `/api/v1/specialists/${specialistId}/availability`,
    specialistAvailabilityResponseSchema,
  );
}

export async function putSpecialistAvailability(
  specialistId: string,
  input: SpecialistAvailabilityPutRequest,
): Promise<SpecialistAvailability> {
  return requestJson(
    `/api/v1/specialists/${specialistId}/availability`,
    specialistAvailabilitySchema,
    jsonInit("PUT", input),
  );
}
